<script setup lang="ts">
import * as z from 'zod'
import type { FormSubmitEvent } from '@nuxt/ui'

const toast = useToast()

const { data: packagesData, pending: packagesPending, error: packagesError } = await useFetch('/api/packages')
const { data: sportsData, pending: sportsPending, error: sportsError } = await useFetch('/api/sports')

const packages = computed(() => packagesData.value?.tiers ?? [])
const sports = computed(() => sportsData.value?.sports ?? [])

const packageItems = computed(() =>
  packages.value.map((p: any) => ({
    label: p.title,
    value: Number(p.id)
  }))
)

const sportItems = computed(() =>
  sports.value.map((s: any) => ({
    label: s.name,
    value: Number(s.id)
  }))
)

const schema = z.object({
  firstname: z.string().trim().min(2, 'Inserisci il nome'),
  lastname: z.string().trim().min(2, 'Inserisci il cognome'),
  email: z.string().trim().email('Email non valida'),
  year: z.coerce
    .number()
    .int()
    .min(1900, 'Anno non valido')
    .max(new Date().getFullYear(), 'Anno non valido')
    .optional(),
  location: z.string().trim().optional(),
  packageId: z.coerce.number().int().positive('Seleziona un pacchetto').min(1, 'Seleziona un pacchetto'),
  sportIds: z.array(z.coerce.number().int().positive()).min(1, 'Seleziona almeno uno sport'),
  // honeypot
  website: z.string().optional()
})

type Schema = z.output<typeof schema>
const state = reactive<Schema>({
  firstname: '',
  lastname: '',
  email: '',
  year: undefined,
  location: '',
  packageId: undefined,
  sportIds: [],
  website: ''
})

const isSubmitting = ref(false)

const isLoading = computed(() => packagesPending.value || sportsPending.value)
const hasLoadError = computed(() => !!packagesError.value || !!sportsError.value)

async function onSubmit(event: FormSubmitEvent<Schema>) {
  try {
    isSubmitting.value = true

    await $fetch('/api/athletes/subscription', {
      method: 'POST',
      body: {
        firstname: event.data.firstname,
        lastname: event.data.lastname,
        email: event.data.email,
        year: event.data.year ?? null,
        location: event.data.location?.length ? event.data.location : null,
        packageId: Number(event.data.packageId),
        sportIds: (event.data.sportIds ?? []).map(n => Number(n)),
        // honeypot (l’API lo gestisce)
        website: event.data.website || ''
      }
    })

    toast.add({
      title: 'Richiesta inviata',
      description: 'Sei in lista: ti contatteremo a breve per completare l’iscrizione.'
    })

    await $fetch('/api/messages', {
      method: 'POST',
      body: {
        title: 'Nuova richiesta di iscrizione',
        message: 'richiesta in stato pending',
        email: state.email,
        firstname: state.firstname,
        lastname: state.lastname,
        sportId: 0,
        packageId: Number(event.data.packageId)
      }
    })

    state.firstname = ''
    state.lastname = ''
    state.email = ''
    state.year = undefined
    state.location = ''
    state.packageId = undefined
    state.sportIds = []
    state.website = ''
  } catch (e: any) {
    toast.add({
      title: 'Errore',
      description: e?.data?.message ?? 'Non sono riuscito a inviare la richiesta. Riprova.'
    })
  } finally {
    isSubmitting.value = false
  }
}

const route = useRoute()

const applyPlan = () => {
  const plan = route.query.plan
  const parsed = Number(plan)
  if (Number.isFinite(parsed) && parsed > 0) state.packageId = parsed
}

onMounted(applyPlan)
watch(() => route.query.plan, applyPlan)
</script>

<template>
  <section
    id="iscrizione"
    class="relative overflow-hidden border-t border-slate-200/70 bg-slate-50 dark:border-slate-800 dark:bg-slate-950"
  >
    <!-- Decorazioni soft (coerenti con la landing) -->
    <div class="pointer-events-none absolute inset-0">
      <div class="absolute -top-20 left-1/2 h-56 w-[38rem] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
      <div class="absolute -bottom-24 left-0 h-56 w-56 rounded-full bg-sky-500/10 blur-3xl" />
    </div>

    <UContainer class="relative py-14 md:py-20">
      <div class="mx-auto max-w-2xl text-center">
        <h2 class="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white md:text-4xl">
          Iscriviti
        </h2>
        <p class="mt-3 text-slate-600 dark:text-slate-300">
          Compila il form, scegli sport (anche più di uno) e pacchetto. La richiesta viene salvata in stato
          <strong>pending</strong>.
        </p>
      </div>

      <div class="mt-10 grid gap-6 lg:grid-cols-12 lg:items-start">
        <!-- Colonna info -->
        <div class="lg:col-span-5">
          <div
            class="rounded-2xl border border-slate-200/70 bg-white/70 p-6 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/40"
          >
            <h3 class="text-base font-semibold text-slate-900 dark:text-white">
              Come funziona
            </h3>

            <ul class="mt-4 space-y-3 text-sm text-slate-600 dark:text-slate-300">
              <li class="flex gap-3">
                <span
                  class="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary"
                >
                  1
                </span>
                <span>Ricevo la richiesta e verifico che sport/pacchetto siano coerenti con l’obiettivo.</span>
              </li>
              <li class="flex gap-3">
                <span
                  class="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary"
                >
                  2
                </span>
                <span>Se fai multisport, va benissimo: seleziona più discipline (es. nuoto + bici + corsa).</span>
              </li>
              <li class="flex gap-3">
                <span
                  class="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary"
                >
                  3
                </span>
                <span>Ti contatto via email per conferma e prossimi step.</span>
              </li>
            </ul>

            <div
              class="mt-6 rounded-xl border border-slate-200/70 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40"
            >
              <p class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Nota
              </p>
              <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                I campi con <span class="text-red-500">*</span> sono obbligatori.
              </p>
            </div>
          </div>
        </div>

        <!-- Colonna form -->
        <div class="lg:col-span-7">
          <div
            class="rounded-2xl border border-slate-200/70 bg-white/80 p-6 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/40"
          >
            <div
              v-if="hasLoadError"
              class="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-200"
            >
              Errore nel caricamento di sport o pacchetti. Ricarica la pagina e riprova.
            </div>

            <UForm
              :schema="schema"
              :state="state"
              class="space-y-5"
              @submit="onSubmit"
            >
              <!-- honeypot: invisibile, ma presente nel DOM -->
              <div class="hidden">
                <UFormField label="Website" name="website">
                  <UInput v-model="state.website" autocomplete="off" />
                </UFormField>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <UFormField name="firstname">
                  <template #label>
                    <span>Nome <span class="text-red-500">*</span></span>
                  </template>
                  <UInput
                    v-model="state.firstname"
                    placeholder="Mario"
                    autocomplete="given-name"
                    icon="i-lucide-user"
                    class="w-full"
                  />
                </UFormField>

                <UFormField name="lastname">
                  <template #label>
                    <span>Cognome <span class="text-red-500">*</span></span>
                  </template>
                  <UInput
                    v-model="state.lastname"
                    placeholder="Rossi"
                    autocomplete="family-name"
                    icon="i-lucide-user"
                    class="w-full"
                  />
                </UFormField>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <UFormField name="email">
                  <template #label>
                    <span>Email <span class="text-red-500">*</span></span>
                  </template>
                  <UInput
                    v-model="state.email"
                    type="email"
                    placeholder="mario.rossi@email.com"
                    autocomplete="email"
                    icon="i-lucide-mail"
                    class="w-full"
                  />
                </UFormField>

                <UFormField label="Anno di nascita (opzionale)" name="year">
                  <UInput
                    v-model="state.year"
                    type="number"
                    placeholder="1998"
                    inputmode="numeric"
                    icon="i-lucide-calendar"
                    class="w-full"
                  />
                </UFormField>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <UFormField label="Città (opzionale)" name="location">
                  <UInput
                    v-model="state.location"
                    placeholder="Milano"
                    autocomplete="address-level2"
                    icon="i-lucide-map-pin"
                    class="w-full"
                  />
                </UFormField>

                <UFormField name="sportIds">
                  <template #label>
                    <span>Sport <span class="text-red-500">*</span></span>
                  </template>
                  <USelect
                    v-model="state.sportIds"
                    multiple
                    :items="sportItems"
                    placeholder="Seleziona una o più discipline..."
                    class="w-full"
                  />
                </UFormField>
              </div>

              <UFormField name="packageId">
                <template #label>
                  <span>Pacchetto <span class="text-red-500">*</span></span>
                </template>
                <USelect
                  v-model="state.packageId"
                  :items="packageItems"
                  placeholder="Seleziona un pacchetto..."
                  class="w-full"
                />
              </UFormField>

              <div class="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-end">
                <UButton
                  type="submit"
                  color="primary"
                  variant="solid"
                  icon="i-lucide-send"
                  :loading="isSubmitting"
                  :disabled="isLoading || hasLoadError"
                  class="w-full sm:w-auto"
                >
                  Invia richiesta
                </UButton>
              </div>

              <p class="text-xs text-slate-500 dark:text-slate-400">
                Inviando questo form accetti di essere ricontattato via email.
              </p>
            </UForm>
          </div>
        </div>
      </div>
    </UContainer>
  </section>
</template>
